import { useState } from 'react';
import { Loader2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Collapsible({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded p-2">
      <button
        className="flex items-center gap-2 w-full text-left text-sm font-medium"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
        {badge && <Badge variant="outline" className="ml-auto text-xs">{badge}</Badge>}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function Pre({ data }) {
  return (
    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-96 whitespace-pre-wrap">
      {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
    </pre>
  );
}

function NettingVerdict({ analysis }) {
  if (!analysis) return null;
  const v = analysis.verdict || 'unknown';
  const variant = v.includes('matches') ? 'default' : v.includes('near') ? 'secondary' : 'destructive';
  return (
    <div className="space-y-1">
      <Badge variant={variant}>{v}</Badge>
      {analysis.candidate_pool && (
        <span className="text-[10px] text-muted-foreground ml-2">pool: {analysis.candidate_pool}</span>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-1">
        {analysis.bank_amount && <div>Bank: {analysis.bank_amount}</div>}
        {analysis.invoice_gross && <div>Gross: {analysis.invoice_gross}</div>}
        {analysis.gross_diff && <div>Gross diff: {analysis.gross_diff}</div>}
        {analysis.gross_minus_15pct && (
          <div>Gross-15%: {analysis.gross_minus_15pct} (diff {analysis.gross_minus_15pct_diff})</div>
        )}
        {analysis.gross_minus_18pct && (
          <div>Gross-18%: {analysis.gross_minus_18pct} (diff {analysis.gross_minus_18pct_diff})</div>
        )}
        {analysis.net_revenue && (
          <div>Net revenue: {analysis.net_revenue} (diff {analysis.net_diff})</div>
        )}
      </div>
    </div>
  );
}

export function AmountDateDiagnosticPanel() {
  const [entityId, setEntityId] = useState('afpv');
  const [daysBack, setDaysBack] = useState(90);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneReconAmountDateDiagnostic({
        entity_id: entityId,
        days_back: daysBack,
        sample_size: 5,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const a1 = result?.a1_branch_condition;
  const a2 = result?.a2_per_payout_analysis || [];
  const a3 = result?.a3_netting_verdict_summary || {};
  const a4 = result?.a4_invoice_existence;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-amber-600" />
          Amount+Date Match Diagnostic
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only analysis of why Booking.com amount+date matching returns 0.
          Reports: branch condition (A-1), per-payout candidate rejection reasons (A-2),
          commission-netting hypothesis (A-3), invoice existence (A-4).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Entity</label>
            <select
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
            >
              <option value="afpv">AFPV Invest (afpv)</option>
              <option value="bi_sas">Bonjour Investments SAS (bi_sas)</option>
              <option value="bi_immo">Bonjour Immobilier (bi_immo)</option>
              <option value="sci_113">SCI 113 Rue Saint-Honore (sci_113)</option>
              <option value="pfv">PFV Holding (pfv)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Days back</label>
            <input
              type="number"
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background w-20"
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value) || 90)}
              min={7}
              max={365}
            />
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Run Diagnostic
          </Button>
        </div>

        {result?.error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded p-2">{result.error}</div>
        )}

        {result?.note_on_pool && (
          <p className="text-xs text-muted-foreground bg-muted rounded p-2">{result.note_on_pool}</p>
        )}

        {result && !result.error && (
          <div className="space-y-4">
            {/* A-1: Branch Condition */}
            <Collapsible title="A-1: Fallback Branch Condition" badge="branch logic" defaultOpen>
              <div className="space-y-2">
                <Pre data={a1?.condition_verbatim} />
                <p className="text-sm">{a1?.answer}</p>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span>Total Booking.com txns: {a1?.total_booking_txns}</span>
                  <span>Keyed: {a1?.keyed_count}</span>
                  <span>Unkeyed: {a1?.unkeyed_count}</span>
                  <span>All reach fallback: {a1?.all_reach_fallback ? 'yes' : 'no'}</span>
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 rounded p-1.5">{a1?.note}</p>
              </div>
            </Collapsible>

            {/* A-3: Netting Verdict Summary */}
            <Collapsible title="A-3: Commission-Netting Verdict Summary" badge={Object.keys(a3).join(', ')} defaultOpen>
              <div className="space-y-1">
                {Object.entries(a3).map(([verdict, count]) => (
                  <div key={verdict} className="flex justify-between text-sm">
                    <span>{verdict}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
              </div>
            </Collapsible>

            {/* A-4: Invoice Existence */}
            <Collapsible title="A-4: Invoice Existence" badge={`${a4?.booking_com_channel_invoices || 0} booking_com`} defaultOpen>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Total open invoices: {a4?.total_open_invoices}</div>
                <div>Non-Airbnb: {a4?.non_airbnb_open_invoices}</div>
                <div>Booking.com channel: {a4?.booking_com_channel_invoices}</div>
                <div>Unknown channel: {a4?.unknown_channel_invoices}</div>
              </div>
              {a4?.booking_com_amount_range && (
                <div className="text-xs mt-1 text-muted-foreground">
                  Amount range: {a4.booking_com_amount_range.min} to {a4.booking_com_amount_range.max} ({a4.booking_com_amount_range.count} invoices)
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{a4?.interpretation}</p>
            </Collapsible>

            {/* A-2: Per-Payout Analysis */}
            {a2.map((row, i) => (
              <Collapsible
                key={row.txn_id || i}
                title={`A-2 Sample ${i + 1}: txn ${row.txn_id} (${row.is_keyed ? 'keyed' : 'unkeyed'})`}
                badge={`${row.bank_amount} ${row.currency}`}
              >
                <div className="space-y-2">
                  <div className="text-xs space-y-0.5">
                    <div>Date: {row.value_date} | Amount: {row.bank_amount} {row.currency}</div>
                    <div className="font-mono text-[11px] break-all">Label: {row.raw_label}</div>
                    {row.parsed_ref && <div>Parsed ref: {row.parsed_ref}</div>}
                    <div>Fallback reason: {row.fallback_reason || 'n/a (ref resolved)'}</div>
                  </div>

                  {/* Rejection summary */}
                  <div>
                    <div className="text-xs font-medium">
                      Candidate pool: {row.candidate_pool_size} invoices, {row.candidates_passed} passed
                    </div>
                    {row.rejection_summary && Object.keys(row.rejection_summary).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(row.rejection_summary).map(([reason, count]) => (
                          <Badge key={reason} variant="outline" className="text-[10px]">
                            {reason}: {count}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Closest by amount */}
                  {row.closest_by_amount?.length > 0 && (
                    <Collapsible title="Closest invoices by amount" badge={`${row.closest_by_amount.length}`}>
                      <div className="overflow-x-auto">
                        <table className="text-[11px] w-full">
                          <thead>
                            <tr className="text-left text-muted-foreground">
                              <th className="pr-2">Inv #</th>
                              <th className="pr-2">Amount</th>
                              <th className="pr-2">Date</th>
                              <th className="pr-2">Channel</th>
                              <th>Rejection</th>
                            </tr>
                          </thead>
                          <tbody>
                            {row.closest_by_amount.map((c, j) => (
                              <tr key={j} className={c.passed ? 'text-green-700' : ''}>
                                <td className="pr-2 font-mono">{c.invoice_number}</td>
                                <td className="pr-2">{c.invoice_amount} {c.invoice_currency}</td>
                                <td className="pr-2">{c.invoice_date}</td>
                                <td className="pr-2">{c.channel}</td>
                                <td className="text-muted-foreground">{c.rejection_reason || 'PASS'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Collapsible>
                  )}

                  {/* Netting analysis */}
                  <div>
                    <div className="text-xs font-medium mb-1">Netting analysis:</div>
                    <NettingVerdict analysis={row.netting_analysis} />
                    {row.netting_analysis?.against_invoice && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Compared against: {row.netting_analysis.against_invoice.invoice_number} ({row.netting_analysis.against_invoice.channel})
                      </div>
                    )}
                  </div>

                  {/* Invoice existence for this payout */}
                  <div className="text-xs text-muted-foreground">
                    Booking.com-tagged invoices in pool: {row.a4_invoice_existence?.booking_com_channel_invoices || 0}
                  </div>
                </div>
              </Collapsible>
            ))}

            {/* Interpretation guide */}
            <Collapsible title="Interpretation Guide">
              <div className="text-xs space-y-1">
                {Object.entries(result.interpretation_guide || {}).map(([key, text]) => (
                  <p key={key}><strong>{key}:</strong> {text}</p>
                ))}
              </div>
            </Collapsible>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
